/**
 * Las notas del silo en las tres estaciones que lo ven (docs/esquema.md §12.4): la lista de las que
 * ya tiene, la caja para escribir una y la mutación que la lleva. Cada estación las pone donde ya ve
 * el silo; ninguna agrega pantallas propias.
 */

import { generarUlid } from '../../src/ulid';
import type { Entidad } from '../datos/bd';
import { encolar, mutacionesDeAvisos, type MutacionAEncolar } from '../datos/cola';
import { notasDelSilo, type NotaVisible } from '../nucleo/avisos';
import { armarPayloadNota, MAX_TEXTO_AVISO, textoValido } from '../nucleo/produccion';
import { cuandoCorto, fechaDeProduccion } from '../nucleo/tiempo';
import { el } from './componentes';
import { ahoraMs, app, recalcularAvisos } from './estado';
import { nombreDe } from './pantallas-captura';

const NOMBRE_ESTACION: Record<string, string> = { produccion: 'producción', calidad: 'calidad', envasado: 'envasado', gestion: 'oficina' };

/** Las notas de un silo, del catálogo y de la cola de este teléfono. */
export function notasDe(llenadoId: string) {
	return notasDelSilo(app.catalogos?.notas_llenado ?? [], app.mutacionesDeAvisos, llenadoId, app.sesion?.dispositivo_id ?? null, app.sesion?.estacion ?? '');
}

/** «· 2 notas» para una tarjeta. */
export function cuantasNotas(llenadoId: string): string {
	const n = notasDe(llenadoId).length;
	return n === 0 ? '' : n === 1 ? ' · 1 nota' : ` · ${n} notas`;
}

/** Tocar «Anular» en una nota propia la deja armada; el segundo toque la anula (sin diálogos del navegador), como el aviso. */
const notasPorAnular = new Set<string>();

/**
 * Anula una nota de este teléfono (§12.4: la anula el mismo dispositivo que la escribió, o gestión).
 * Una que todavía no subió también: la cola manda la creación y después la anulación.
 */
async function anularNota(n: NotaVisible, precinto: string | null): Promise<void> {
	const persona = app.sesion?.persona_habitual_id ?? n.persona_id ?? null;
	if (persona === null) return;
	await encolar(
		app.bd,
		[
			{
				id: generarUlid(),
				tipo: 'nota_llenado.anular',
				payload: { nota_id: n.id, anulado_por: persona, motivo: 'anulada por quien la escribió' },
				entidad: 'nota_llenado' as Entidad,
				registro_id: n.id,
				personas: [persona],
				resumen: { entidad: 'nota_llenado', fecha: fechaDeProduccion(ahoraMs()), accion: 'anulo', precinto: precinto ?? '—', texto: n.texto },
			},
		],
		[],
		new Date(ahoraMs()).toISOString(),
	);
	notasPorAnular.delete(n.id);
	recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
	app.dispararSync();
	app.render();
}

/** Las notas que ya tiene el silo, la más vieja primero; las de este teléfono, con «Anular». Nada si no tiene. */
export function listaDeNotas(cuerpo: HTMLElement, llenadoId: string | null, precinto: string | null = null): void {
	if (llenadoId === null) return;
	const notas = notasDe(llenadoId);
	if (notas.length === 0) return;
	const caja = el('div', { class: 'notas' });
	for (const n of notas) {
		const fila = el('div', { class: 'nota-silo' });
		fila.appendChild(el('b', undefined, n.texto));
		const pie = el('div', { class: 'pie-aviso' });
		pie.appendChild(
			el('small', undefined, `${n.persona_id ? nombreDe(n.persona_id) : 'oficina'} · ${NOMBRE_ESTACION[n.estacion] ?? n.estacion} · ${cuandoCorto(n.registrado_en, ahoraMs())}${n.local ? ' · sin subir' : ''}`),
		);
		if (n.propia) {
			const armada = notasPorAnular.has(n.id);
			const boton = el('button', { class: armada ? 'chip sel' : 'chip', type: 'button' }, armada ? 'Tocá de nuevo para anular' : 'Anular');
			boton.onclick = () => {
				if (!notasPorAnular.has(n.id)) {
					notasPorAnular.add(n.id);
					app.render();
					return;
				}
				void anularNota(n, precinto);
			};
			pie.appendChild(boton);
		}
		fila.appendChild(pie);
		caja.appendChild(fila);
	}
	cuerpo.appendChild(caja);
}

/** Una caja de texto libre de hasta 400 caracteres que guarda en cada tecla. */
export function cajaDeTexto(cuerpo: HTMLElement, leer: () => string | null | undefined, escribir: (v: string) => void, ejemplo: string, guardar: () => void): void {
	const texto = el('textarea', { class: 'nota', rows: '3', maxlength: String(MAX_TEXTO_AVISO), placeholder: ejemplo });
	texto.value = leer() ?? '';
	texto.oninput = () => {
		escribir(texto.value);
		guardar();
	};
	cuerpo.appendChild(texto);
}

/** La mutación de una nota nueva. `padre`: la apertura del silo, si sigue en la cola de este teléfono. */
export function notaParaLaCola(
	mutacionId: string,
	notaId: string,
	llenadoId: string | null,
	precinto: string | null,
	texto: string | null | undefined,
	personas: string[],
	padre: string | null = null,
): MutacionAEncolar {
	const ahora = ahoraMs();
	return {
		id: mutacionId,
		personas: [...personas],
		tipo: 'nota_llenado.crear',
		payload: armarPayloadNota(notaId, llenadoId, texto, personas[0], ahora),
		entidad: 'nota_llenado' as Entidad,
		registro_id: notaId,
		...(padre === null ? {} : { padre_id: padre }),
		resumen: { entidad: 'nota_llenado', fecha: fechaDeProduccion(ahora), accion: 'escribio', precinto: precinto ?? '—', texto: textoValido(texto) ?? '' },
	};
}
